import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, Search, User, LogOut, Shield, Package, Heart, UserCog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Logo } from "@/components/Logo";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const linkCls = (isActive) =>
  `font-display uppercase tracking-wider text-sm font-bold transition-colors duration-200 ${isActive ? "text-[#FF3B30]" : "text-neutral-300 hover:text-white"}`;

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  const submit = (e) => { e.preventDefault(); nav(`/shop?q=${encodeURIComponent(q)}`); };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-neutral-950/80 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-6">
        <Link to="/" data-testid="nav-logo"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-5 ml-4">
          {[["/shop", "", "Shop", "nav-shop-link"], ["/shop?category=apparel", "apparel", "Apparel", "nav-apparel-link"], ["/shop?category=hats", "hats", "Hats", "nav-hats-link"], ["/shop?category=programs", "programs", "Programs", "nav-programs-link"], ["/shop?category=supplements", "supplements", "Supplements", "nav-supplements-link"], ["/shop?category=meals", "meals", "Meal Plans", "nav-meals-link"]].map(([to, cat, label, id]) => (
            <Link key={id} to={to} data-testid={id} className={linkCls(loc.pathname === "/shop" && (new URLSearchParams(loc.search).get("category") || "") === cat)}>{label}</Link>
          ))}
        </nav>
        <form onSubmit={submit} className="ml-auto hidden sm:flex items-center bg-white/5 border border-white/10 focus-within:border-[#FF3B30] transition-colors px-3 h-10 w-64">
          <Search className="w-4 h-4 text-neutral-500" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search gear..."
            className="bg-transparent outline-none text-sm px-2 w-full placeholder:text-neutral-500" />
        </form>
        <div className="flex items-center gap-2 ml-auto sm:ml-0">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-user-menu" className="h-10 w-10 grid place-items-center hover:bg-white/10 hover:text-white transition-colors">
                <User className="w-5 h-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-[#121215] border-white/10">
                <div className="px-2 py-2 text-xs font-mono text-neutral-400 truncate">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-orders-link" onClick={() => nav("/orders")}><Package className="w-4 h-4 mr-2" />My Orders</DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-wishlist-link" onClick={() => nav("/wishlist")}><Heart className="w-4 h-4 mr-2" />Wishlist{user.wishlist_ids?.length > 0 && <span className="ml-auto font-mono text-[10px] text-neutral-400">{user.wishlist_ids.length}</span>}</DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-profile-link" onClick={() => nav("/profile")}><UserCog className="w-4 h-4 mr-2" />Profile</DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="nav-admin-link" onClick={() => nav("/admin")}><Shield className="w-4 h-4 mr-2" />Admin</DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="nav-logout-button" onClick={logout}><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="nav-login-link" className="font-display uppercase font-bold text-sm tracking-wider px-3 h-10 grid place-items-center text-neutral-300 hover:text-white">
              Sign in
            </Link>
          )}
          <button data-testid="nav-cart-button" onClick={() => setOpen(true)} className="relative h-10 w-10 grid place-items-center bg-[#FF3B30] hover:bg-[#E02E24] transition-colors">
            <ShoppingBag className="w-5 h-5 text-white" />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-2 -right-2 min-w-5 h-5 px-1 grid place-items-center bg-white text-black text-[11px] font-mono font-bold">{count}</span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};
